import { SHIELD_API_KEY } from 'constants';
import { Api, AppEnvs, AuthTypes, ContentType, ContentStatus, SourceType } from 'enums';
import { Authenticator, streamHasAuthType } from 'lib/auth';
import { getPlaybackMeta } from 'lib/api/PageAPI';
import cookie from 'cookie-cutter';
import getAdobeDCRConfig from './getAdobeDCRConfig';
import getPrerollSetting from './getPrerollSetting';
import {
    isEmpty,
    getDeviceTypeFromUA,
    getSiteSectionId
} from 'lib/util/index';
import WatchESPNWebPlayer from 'webapp/common/video-player';

const { Env: ApiEnv } = Api;
let listing;

function isGoogleAdProvider(providerName) {
    return typeof providerName === 'string' && providerName.toLowerCase().indexOf('google') !== -1;
}

function appendToken(url, { token, tokenType, resource } = {}, apiKey = SHIELD_API_KEY) {
    let newUrl = url;
    if (
        token &&
        url &&
        url.indexOf('startSession') === -1
    ) {
        let tokenParam = `&token=${token}`;
        let resourceParam = '';

        if (tokenType === 'ADOBEPASS') {
            tokenParam = `&adobeToken=${token}`;
            resourceParam = `&adobeResource=${resource}`;
        }

        // TODO: remove once `videoHref` points to `https`
        newUrl = newUrl.replace('http:', 'https:');
        newUrl += `${newUrl.indexOf('?') === -1 ? '?' : '&'}apikey=${apiKey}${tokenParam}${resourceParam}`;
    }

    return newUrl;
}

export default ({
    apiEnv,
    stream,
    authorization = {},
    streamlimiter,
    nielsenTrackingData = {},
    comscoreTrackingData,
    isMobile = false,
    device,
    error = null,
    env,
    lang,
    provider = {},
    isDomestic,
    social,
    logo,
    settingsMenu,
    feedsMenu,
    userInitiated,
    playbackMeta,
    prerollConfig,
    adsConfig,
    userAuth = [],
    shieldApiKey
}) => {
    const { content } = stream;
    const { name: affiliate } = provider;

    const autoplay = 'on';
    // always user initiated on mobile
    // TODO: perhaps Hero should be wrapped in connectPlayer as well as playable
    const wasUserInitiated = isMobile ? true : !!userInitiated;
    let conviva = null;

    const playerTech = streamHasAuthType(stream, AuthTypes.BAM) ?
                    WatchESPNWebPlayer.PlayerTech.BAM : WatchESPNWebPlayer.PlayerTech.BRIGHTCOVE;
    const analytics = {
        adobeDCR: getAdobeDCRConfig(nielsenTrackingData, {}, env, playerTech),
        comscore: comscoreTrackingData,
        chartbeat: {
            uid: '22222',
            domain: 'espn.com'
        }
    };

    // for easy debugging
    let opts = null;
    let auth;

    const authToken = authorization.token;
    if (authToken) {
        auth = {
            token: authorization.encoded ? authToken : window.btoa(unescape(encodeURIComponent(authToken))),
            resource: window.btoa(unescape(encodeURIComponent(authorization.resourceId || ''))),
            tokenType: authorization.tokenType || 'ADOBEPASS',
            partnerName: authorization.partnerName,
            userAuth
        };
    }

    if (content.type === ContentType.VOD) {
        const moreOptions = {};
        if (stream.playlist && stream.playlist.length > 0) {
            moreOptions.playlist = stream.playlist;
        } else {
            moreOptions.clipId = stream.id;
        }

        if (env === AppEnvs.prod) {
            conviva = {
                customerKey: 'fc8dbfdc5da519f576628f619306a357f31bd006'
            };
        }

        opts = {
            ...moreOptions,
            affiliate,
            language: lang,
            advertising: {
                siteSectionId: getSiteSectionId(getDeviceTypeFromUA(), device, 'clips')
            },
            analytics: {
                ...analytics,
                conviva
            },
            auth,
            autoplay,
            social,
            logo,
            settingsMenu,
            feedsMenu,
            endcard: true,
            streamlimiter,
            wasUserInitiated
        };
    } else if (content.type === ContentType.LISTING) {
        const { source: { type: sourceType, id: sourceId }, id, status } = stream;
        const asset = {
            channelId: sourceId
        };
        if (sourceType === SourceType.ONLINE || status === ContentStatus.REPLAY) {
            asset.watchId = id;
        }

        if (env === AppEnvs.prod) {
            conviva = {
                customerKey: '766ee8daa5d5deaeeeab4481239072ea53f6f1cc'
            };
        }

        const api = (apiEnv === ApiEnv.QA) ? {
            WATCH_BASE_URL: '//broadband.espnqa.com/espn360/apis/v1/watch'
        } : {};

        opts = {
            affiliate,
            language: lang,
            autoplay,
            social,
            logo,
            settingsMenu,
            feedsMenu,
            advertising: {
                assetId: stream.id
            },
            analytics: {
                ...analytics,
                conviva
            },
            api,
            wasUserInitiated,
            ...asset,
            auth,
            streamlimiter,
            error
        };
    }

    if (!isEmpty(prerollConfig)) {
        opts.preroll = getPrerollSetting(prerollConfig, content.id);
    }

    if (!opts) {
        return opts;
    }

    opts.api = opts.api || {};
    opts.api.shieldApiKey = shieldApiKey || SHIELD_API_KEY;

    const shouldOverrideListings = (
        !error &&
        stream.authTypes && stream.authTypes.length &&
        stream.authTypes.some(authType => [AuthTypes.MVPD, AuthTypes.OPEN].indexOf(authType) > -1) &&
        stream.links && stream.links.play
    );

    let initialPlaybackMeta = { ...playbackMeta };

    const listings = shouldOverrideListings ? async (callback, context = false) => {
        const { auth: { tokenType } = {} } = opts;
        if (listing && context && tokenType === 'ADOBEPASS') {
            // the very initial Authenticator factory call sends the configuration
            const authenticator = Authenticator.adobe();
            const { adobeRSS, origVideoHref } = listing;
            if (!adobeRSS) {
                callback(null, listing);
                return;
            }
            try {
                const response = await authenticator.authorize(adobeRSS);
                const token = window.btoa(unescape(encodeURIComponent(response.token)));
                const resource = window.btoa(unescape(encodeURIComponent(response.resourceId || '')));
                opts.auth = { token, resource, tokenType };
                const videoHref = appendToken(origVideoHref, opts.auth, shieldApiKey);
                listing = { ...listing, videoHref };
                callback(null, listing);
            } catch (err) {
                callback(err);
            }
            return;
        }

        try {
            // re-use the `playbackMeta` data that's retrieved upon `streamSelect` for the first time
            const response = !isEmpty(initialPlaybackMeta) ? initialPlaybackMeta : await getPlaybackMeta(stream.links.play);
            // `fallbackCache` returns `null` instead of `undefined`, so can't use default value
            const { playbackState, adobeRSS } = response || {};
            const { videoHref: origVideoHref } = playbackState || {};
            if (!origVideoHref) {
                callback('No playback URL found', null); // TODO: localize message or move this check in the player
                return;
            }
            const videoHref = appendToken(origVideoHref, opts.auth, shieldApiKey);
            listing = { ...playbackState, origVideoHref, videoHref, adobeRSS };
            initialPlaybackMeta = null;
            callback(null, listing);
        } catch (err) {
            callback(err);
        }
    } : null;

    const programChangePollingInterval = (
        shouldOverrideListings &&
        !isDomestic &&
        stream.source.type === SourceType.LINEAR
    ) ? 60 * 1000 : null;

    // this won't technically happen right now,
    // but remove `playlist` and `clipId` so that `listings` value is respected in the player
    if (listings) {
        opts.playlist = null;
        opts.clipId = null;
    }

    // NOTE: The adsConfig is handled by the external app-configuration file via Bootstrap
    // See: https://code.espn.com/connected-devices/app-configurations/blob/master/configurations/watchespn-web-3.0.config.json
    if (!isEmpty(adsConfig)) {
        const usPrivacyCookie = cookie.get('usprivacy') || '';
        const { activeAdProvider, allowAdSkip, useNewUI, iphoneplaysinline } = adsConfig;
        // NOTE: Sets the Ad Provider for the Player instance
        opts.adProviderOrder = activeAdProvider;
        opts.advertising.allowAdSkip = allowAdSkip;
        opts.advertising.useNewUI = useNewUI;
        opts.iphoneplaysinline = iphoneplaysinline;

        if (isGoogleAdProvider(activeAdProvider)) {
            const { cmsid, networkCode, pp, adUnit } = adsConfig;

            opts.advertising.googleIMA = (_options) => {
                const options = _options || {};
                const imaParams = options.imaParams || {};
                const custParams = imaParams.cust_params || {};

                if (cmsid) {
                    options.cmsid = cmsid;
                }

                // NOTE: `adUnit` and `networkCode` will be used for MOAT tracking
                if (networkCode) {
                    options.networkCode = networkCode;
                }

                if (adUnit) {
                    options.adUnit = adUnit;
                }

                // NOTE: INTL editions will have `pp` property from config
                if (pp) {
                    options.pp = (isMobile) ? pp.mobile : pp.desktop;
                }

                if (usPrivacyCookie) {
                    custParams.d_us_privacy = usPrivacyCookie;
                    imaParams.us_privacy = usPrivacyCookie;
                    if (usPrivacyCookie === '1YYY') {
                        imaParams.rdp = '1';
                    }
                    imaParams.cust_params = custParams;
                    options.imaParams = imaParams;
                }

                return options;
            };

            if (content.type === ContentType.LISTING && opts.auth) {
                opts.auth.adEnv = (env === AppEnvs.prod) ? 'prod' : 'dev';
                opts.auth.adProvider = activeAdProvider;
            }
        }
    }

    // Hardcoding to false for now since the config affects both v2 and v3 player
    opts.enableDRM = false; // playerOpts.enableDRM;

    return {
        ...opts,
        listings,
        programChangePollingInterval,
        newStreamPicker: true
    };
};



// WEBPACK FOOTER //
// ./app/webapp/player/util/getPlayerOptions.js